import { useCallback, useEffect, useRef, useState } from "react";
import { useReactFlow } from "@xyflow/react";
import { useCanvasStore } from "@/store/useCanvasStore";
import { CV } from "@/config/canvasSizing";
import { CURSORS } from "@/config/cursors";
import { colorIconClass } from "@/config/theme";
import { hitTestStroke, parseDrawing, strokePath, straighten, type Point } from "@/utils/drawing";

// Transparent overlay above the ReactFlow pane. Active only for the pencil
// (freehand capture) and eraser (whole-stroke delete) tools. Points are captured
// in client coords for a live screen-space preview, then mapped to flow coords on
// commit via screenToFlowPosition so strokes pan/zoom correctly.
export default function DrawingLayer() {
  const activeTool        = useCanvasStore((s) => s.activeTool);
  const pencilColor       = useCanvasStore((s) => s.pencilColor);
  const pencilWidth       = useCanvasStore((s) => s.pencilWidth);
  const pencilHighlighter = useCanvasStore((s) => s.pencilHighlighter);
  const { screenToFlowPosition, getViewport, setViewport } = useReactFlow();

  // In-progress stroke, client coords. previewPts (local coords) drives re-render.
  const strokeRef = useRef<{ active: boolean; client: Point[]; rect: DOMRect | null }>({
    active: false, client: [], rect: null,
  });
  const erasedRef = useRef<Set<string>>(new Set());
  const [previewPts, setPreviewPts] = useState<Point[]>([]);
  // Whether the live preview should render a straight segment (Shift held).
  const [shiftHeld, setShiftHeld] = useState(false);

  // Active touch pointers (client coords) — a single finger draws/erases, two
  // fingers pan + pinch-zoom the canvas instead. gestureRef is non-null only
  // while a two-finger pan/zoom is in progress.
  const pointersRef = useRef<Map<number, Point>>(new Map());
  const gestureRef = useRef<{
    anchor: { x: number; y: number };  // flow coords under the pinch centroid at start
    startDist: number;                 // finger separation at gesture start (px)
    startZoom: number;                 // viewport zoom at gesture start
    rectLeft: number;
    rectTop: number;
  } | null>(null);

  // Begin (or re-seed) a two-finger gesture from the first two active pointers.
  const startGesture = useCallback((svg: SVGSVGElement) => {
    const pts = [...pointersRef.current.values()];
    if (pts.length < 2) return;
    const [a, b] = pts;
    const cx = (a[0] + b[0]) / 2;
    const cy = (a[1] + b[1]) / 2;
    const rect = svg.getBoundingClientRect();
    gestureRef.current = {
      anchor: screenToFlowPosition({ x: cx, y: cy }),
      startDist: Math.hypot(a[0] - b[0], a[1] - b[1]) || 1,
      startZoom: getViewport().zoom,
      rectLeft: rect.left,
      rectTop: rect.top,
    };
  }, [screenToFlowPosition, getViewport]);

  // Apply the current two-finger positions as a pan + pinch-zoom, keeping the
  // flow point anchored under the pinch centroid pinned as fingers move.
  const applyGesture = useCallback(() => {
    const g = gestureRef.current;
    if (!g) return;
    const pts = [...pointersRef.current.values()];
    if (pts.length < 2) return;
    const [a, b] = pts;
    const cx = (a[0] + b[0]) / 2;
    const cy = (a[1] + b[1]) / 2;
    const dist = Math.hypot(a[0] - b[0], a[1] - b[1]) || 1;
    const zoom = Math.min(CV.zoom.max, Math.max(CV.zoom.min, g.startZoom * (dist / g.startDist)));
    setViewport({
      x: (cx - g.rectLeft) - g.anchor.x * zoom,
      y: (cy - g.rectTop) - g.anchor.y * zoom,
      zoom,
    });
  }, [setViewport]);

  const isPencil = activeTool === "pencil";
  const isEraser = activeTool === "eraser";

  const eraseAt = useCallback((clientX: number, clientY: number) => {
    const flow = screenToFlowPosition({ x: clientX, y: clientY });
    const { shapes, deleteElements } = useCanvasStore.getState();
    const hits: string[] = [];
    for (const s of shapes) {
      if (s.kind !== "drawing" || erasedRef.current.has(s.id)) continue;
      const parsed = parseDrawing(s.content);
      if (!parsed) continue;
      const tol = parsed.width / 2 + CV.drawing.erasePadding;
      if (hitTestStroke(parsed.points, [flow.x - s.x, flow.y - s.y], tol)) hits.push(s.id);
    }
    if (hits.length > 0) {
      hits.forEach((id) => erasedRef.current.add(id));
      deleteElements({ shapeIds: hits });
    }
  }, [screenToFlowPosition]);

  const onPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointersRef.current.set(e.pointerId, [e.clientX, e.clientY]);
    // Second finger → switch to pan/zoom, discarding any in-progress stroke.
    if (pointersRef.current.size >= 2) {
      strokeRef.current = { active: false, client: [], rect: null };
      erasedRef.current = new Set();
      setPreviewPts([]);
      startGesture(e.currentTarget);
      return;
    }
    // Single finger begins a stroke — dismiss the pencil options so they stay
    // out of the way. Re-tap the pencil tool to bring them back.
    useCanvasStore.getState().setPencilOptionsOpen(false);
    if (isEraser) {
      strokeRef.current = { active: true, client: [], rect: null };
      erasedRef.current = new Set();
      eraseAt(e.clientX, e.clientY);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    strokeRef.current = { active: true, client: [[e.clientX, e.clientY]], rect };
    setShiftHeld(e.shiftKey);
    setPreviewPts([[e.clientX - rect.left, e.clientY - rect.top]]);
  }, [isEraser, eraseAt, startGesture]);

  const onPointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (gestureRef.current) {
      if (!pointersRef.current.has(e.pointerId)) return;
      pointersRef.current.set(e.pointerId, [e.clientX, e.clientY]);
      applyGesture();
      return;
    }
    if (!strokeRef.current.active) return;
    if (isEraser) { eraseAt(e.clientX, e.clientY); return; }
    setShiftHeld(e.shiftKey);
    strokeRef.current.client.push([e.clientX, e.clientY]);
    const rect = strokeRef.current.rect;
    if (rect) setPreviewPts((prev) => [...prev, [e.clientX - rect.left, e.clientY - rect.top]]);
  }, [isEraser, eraseAt, applyGesture]);

  const onPointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    pointersRef.current.delete(e.pointerId);
    if (gestureRef.current) {
      // Lifting one finger ends the gesture. Never resume drawing with a
      // leftover finger — a fresh stroke starts only on a clean single-finger
      // pointerdown once all fingers are up.
      if (pointersRef.current.size < 2) {
        gestureRef.current = null;
        strokeRef.current = { active: false, client: [], rect: null };
        setPreviewPts([]);
      }
      return;
    }
    if (!strokeRef.current.active) return;
    const wasEraser = isEraser;
    const client = strokeRef.current.client;
    strokeRef.current = { active: false, client: [], rect: null };
    setPreviewPts([]);
    setShiftHeld(false);
    if (wasEraser) { erasedRef.current = new Set(); return; }

    let flow: Point[] = client.map((p) => {
      const f = screenToFlowPosition({ x: p[0], y: p[1] });
      return [f.x, f.y];
    });
    if (e.shiftKey) flow = straighten(flow);
    if (flow.length < 2) return;
    useCanvasStore.getState().addDrawing(flow, {
      color: pencilColor,
      width: pencilWidth,
      highlighter: pencilHighlighter,
    });
  }, [isEraser, screenToFlowPosition, pencilColor, pencilWidth, pencilHighlighter]);

  // Browser cancelled the pointer (e.g. gesture stolen by the OS): drop it and
  // discard any in-progress stroke/gesture without committing.
  const onPointerCancel = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) gestureRef.current = null;
    strokeRef.current = { active: false, client: [], rect: null };
    erasedRef.current = new Set();
    setPreviewPts([]);
    setShiftHeld(false);
  }, []);

  // Update the straight-line preview when Shift is pressed/released without
  // moving the pointer, but only while a stroke is in progress.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== "Shift" || !strokeRef.current.active) return;
      setShiftHeld(ev.type === "keydown");
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
    };
  }, []);

  if (!isPencil && !isEraser) return null;

  const displayPts = shiftHeld ? straighten(previewPts) : previewPts;

  return (
    <svg
      data-testid="drawing-layer"
      className="absolute inset-0 w-full h-full"
      style={{ zIndex: 4, cursor: isEraser ? CURSORS.eraser : CURSORS.pen, touchAction: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {isPencil && displayPts.length > 0 && (
        <path
          className={colorIconClass(pencilColor)}
          d={strokePath(displayPts, true)}
          fill="none"
          stroke="currentColor"
          strokeWidth={pencilWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={pencilHighlighter ? CV.drawing.highlighterOpacity : 1}
        />
      )}
    </svg>
  );
}
